<template>
  <div class="change-pw-container">
    <div class="page-header">
      <div class="breadcrumb">ຕັ້ງຄ່າ / <span>ປ່ຽນລະຫັດຜ່ານ</span></div>
      <h1><i class="fas fa-lock"></i> ປ່ຽນລະຫັດຜ່ານໃໝ່</h1>
      <p>ອັບເດດລະຫັດຜ່ານຂອງທ່ານເພື່ອຄວາມປອດໄພ</p>
    </div>

    <div class="card-small">
      <form @submit.prevent="handleUpdatePassword">

        <div class="form-group">
          <label><i class="fas fa-key"></i> ລະຫັດຜ່ານໃໝ່ <span class="req">*</span></label>
          <div class="pw-wrap">
            <input :type="showNew ? 'text' : 'password'" v-model="form.newPwd"
                   placeholder="ຢ່າງໜ້ອຍ 6 ຕົວອັກສອນ" autocomplete="new-password" />
            <button type="button" class="pw-toggle" @click="showNew = !showNew">
              <i :class="showNew ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div v-if="form.newPwd && form.newPwd.length < 6" class="field-err">
            ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ 6 ຕົວ
          </div>
        </div>

        <div class="form-group">
          <label><i class="fas fa-check-double"></i> ຢືນຢັນລະຫັດຜ່ານໃໝ່ <span class="req">*</span></label>
          <div class="pw-wrap">
            <input :type="showConfirm ? 'text' : 'password'" v-model="form.confirmPwd"
                   placeholder="ປ້ອນລະຫັດຜ່ານໃໝ່ອີກຄັ້ງ" autocomplete="new-password" />
            <button type="button" class="pw-toggle" @click="showConfirm = !showConfirm">
              <i :class="showConfirm ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div v-if="form.confirmPwd && form.newPwd !== form.confirmPwd" class="field-err">
            ລະຫັດຜ່ານບໍ່ກົງກັນ
          </div>
        </div>

        <!-- password strength bar -->
        <div v-if="form.newPwd" class="strength-bar">
          <div class="strength-fill" :class="strengthClass" :style="{ width: strengthPct + '%' }"></div>
        </div>
        <div v-if="form.newPwd" class="strength-label" :class="strengthClass">
          {{ strengthText }}
        </div>

        <button type="submit" class="btn-save" :disabled="updating || !isValid">
          <i class="fas" :class="updating ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          {{ updating ? 'ກຳລັງອັບເດດ...' : 'ຢືນຢັນການປ່ຽນລະຫັດຜ່ານ' }}
        </button>

        <button type="button" class="btn-cancel" @click="$router.go(-1)">
          ຍົກເລີກ
        </button>

      </form>
    </div>

    <transition name="toast-slide">
      <div v-if="toast.show" :class="['toast', toast.type]">
        <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
        {{ toast.msg }}
      </div>
    </transition>
  </div>
</template>

<script>
import api  from '@/utils/api';
import auth from '@/utils/auth';

export default {
  name: 'ChangePasswordPage',
  data() {
    return {
      updating: false,
      showNew: false,
      showConfirm: false,
      form: { newPwd: '', confirmPwd: '' },
      toast: { show: false, type: 'success', msg: '' },
    };
  },
  computed: {
    isValid() {
      return this.form.newPwd.length >= 6 && this.form.newPwd === this.form.confirmPwd;
    },
    strengthPct() {
      const len = this.form.newPwd.length;
      if (len === 0) return 0;
      if (len < 6)  return 20;
      if (len < 8)  return 45;
      const hasUpper = /[A-Z]/.test(this.form.newPwd);
      const hasNum   = /[0-9]/.test(this.form.newPwd);
      const hasSym   = /[^A-Za-z0-9]/.test(this.form.newPwd);
      const score    = [hasUpper, hasNum, hasSym].filter(Boolean).length;
      return 60 + score * 13;
    },
    strengthClass() {
      const p = this.strengthPct;
      if (p < 40) return 'weak';
      if (p < 65) return 'fair';
      if (p < 85) return 'good';
      return 'strong';
    },
    strengthText() {
      const m = { weak: 'ອ່ອນ', fair: 'ພໍໃຊ້', good: 'ດີ', strong: 'ດີຫຼາຍ' };
      return m[this.strengthClass];
    },
  },
  methods: {
    async handleUpdatePassword() {
      if (!this.isValid) return;
      const user = auth.getUserInfo();
      if (!user || !user._id) {
        this.showToast('error', 'ບໍ່ພົບຂໍ້ມູນຜູ້ໃຊ້ — ກະລຸນາ Login ໃໝ່');
        return;
      }
      this.updating = true;
      try {
        const res = await api.post('/users/change-password', {
          id:       user._id,
          password: this.form.newPwd,
        });
        if (res.data.code === 1) {
          this.showToast('success', 'ປ່ຽນລະຫັດຜ່ານສຳເລັດ');
          this.form = { newPwd: '', confirmPwd: '' };
          setTimeout(() => this.$router.go(-1), 1800);
        } else {
          this.showToast('error', res.data.desc || res.data.msg || 'ເກີດຂໍ້ຜິດພາດ');
        }
      } catch (e) {
        console.error(e);
        this.showToast('error', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີ');
      } finally {
        this.updating = false;
      }
    },
    showToast(type, msg) {
      this.toast = { show: true, type, msg };
      setTimeout(() => { this.toast.show = false; }, 3500);
    },
  },
};
</script>

<style scoped>
.change-pw-container { animation: fadeIn .3s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

.page-header h1 { color: #311B92; font-size: 1.4rem; margin: 4px 0; display: flex; align-items: center; gap: 8px; }
.page-header p  { color: var(--text3); font-size: 13px; margin: 0; }

.card-small {
  background: #fff; padding: 28px 32px; border-radius: 16px; max-width: 460px;
  margin: 24px auto 0; box-shadow: 0 4px 20px rgba(0,0,0,.08);
}

.form-group { margin-bottom: 20px; }
.form-group label { display: flex; align-items: center; gap: 7px; font-size: 13px;
                    font-weight: 600; color: var(--text2); margin-bottom: 8px; }
.form-group label i { color: #4527A0; font-size: 12px; }
.req { color: #e53e3e; }

.pw-wrap { position: relative; }
.pw-wrap input {
  width: 100%; padding: 10px 40px 10px 12px;
  border: 1px solid var(--border); border-radius: 8px; font-size: 14px;
  transition: border .2s; box-sizing: border-box;
}
.pw-wrap input:focus { border-color: #4527A0; outline: none; box-shadow: 0 0 0 3px rgba(69,39,160,.1); }
.pw-toggle { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
             background: none; border: none; color: var(--text3); cursor: pointer; font-size: 14px; }
.field-err { font-size: 11px; color: #C62828; margin-top: 5px; }

/* Strength bar */
.strength-bar { height: 5px; background: #eee; border-radius: 3px; margin: 8px 0 4px; overflow: hidden; }
.strength-fill { height: 100%; border-radius: 3px; transition: width .3s, background .3s; }
.strength-fill.weak   { background: #e53e3e; }
.strength-fill.fair   { background: #dd6b20; }
.strength-fill.good   { background: #2b6cb0; }
.strength-fill.strong { background: #1a7f4b; }
.strength-label { font-size: 11px; font-weight: 600; margin-bottom: 16px; }
.strength-label.weak   { color: #e53e3e; }
.strength-label.fair   { color: #dd6b20; }
.strength-label.good   { color: #2b6cb0; }
.strength-label.strong { color: #1a7f4b; }

.btn-save {
  width: 100%; padding: 12px; margin-top: 4px;
  background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  box-shadow: 0 4px 14px rgba(69,39,160,.35); transition: all .2s;
}
.btn-save:hover:not(:disabled) { box-shadow: 0 6px 18px rgba(69,39,160,.45); }
.btn-save:disabled { opacity: .5; cursor: not-allowed; }

.btn-cancel {
  width: 100%; margin-top: 10px; padding: 10px;
  background: none; border: 1px solid var(--border); color: var(--text2);
  border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background .18s;
}
.btn-cancel:hover { background: #f8f9fa; }

/* Toast */
.toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 2000;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; border-radius: 12px; font-size: 14px; font-weight: 600;
  color: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.18); min-width: 240px;
}
.toast.success { background: linear-gradient(135deg, #1B5E20, #2E7D32); }
.toast.error   { background: linear-gradient(135deg, #B71C1C, #C62828); }
.toast-slide-enter-active, .toast-slide-leave-active { transition: all .3s ease; }
.toast-slide-enter { opacity: 0; transform: translateX(40px); }
.toast-slide-leave-to { opacity: 0; transform: translateX(40px); }
</style>
